import type { Editor } from '@tiptap/react';

import { useEditorState } from '@tiptap/react';
import { BoldIcon, Code2Icon, ItalicIcon, StrikethroughIcon, SubscriptIcon, SuperscriptIcon, UnderlineIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTiptapEditor } from '@/hooks/tiptap';
import { isMarkInSchema } from '@/lib/tiptap/tiptapUtils';

import type { MarkButtonProps, Mark } from '.';

export const MARK_ICONS = {
  bold: BoldIcon,
  italic: ItalicIcon,
  underline: UnderlineIcon,
  strike: StrikethroughIcon,
  code: Code2Icon,
  superscript: SuperscriptIcon,
  subscript: SubscriptIcon,
};

export const MARK_SHORTCUTS: Partial<Record<Mark, string>> = {
  bold: 'Ctrl-b',
  italic: 'Ctrl-i',
  underline: 'Ctrl-u',
  strike: 'Ctrl-Shift-s',
  code: 'Ctrl-e',
  superscript: 'Ctrl-.',
  subscript: 'Ctrl-,',
};

function useCanToggleMark(editor: Editor | null, type: Mark): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => ctx.editor?.can().toggleMark(type),
    }) ?? false
  );
}

function useIsMarkActive(editor: Editor | null, type: Mark): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => ctx.editor?.isActive(type),
    }) ?? false
  );
}

function toggleMark(editor: Editor | null, type: Mark): void {
  if (!editor) return;
  editor.chain().focus().toggleMark(type).run();
}

function useIsMarkButtonDisabled(editor: Editor | null, type: Mark, userDisabled: boolean = false): boolean {
  const can: boolean = useCanToggleMark(editor, type);

  if (!editor) return true;
  if (userDisabled) return true;
  if (editor.isActive('codeBlock')) return true;
  if (!can) return true;
  return false;
}

function shouldShowMarkButton(editor: Editor | null, markInSchema: boolean): boolean {
  if (!markInSchema || !editor) return false;
  return true;
}

function getFormattedMarkName(type: Mark): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function useMarkState(editor: Editor | null, type: Mark, disabled: boolean = false) {
  const markInSchema: boolean = isMarkInSchema(type, editor);
  const isDisabled: boolean = useIsMarkButtonDisabled(editor, type, disabled);
  const isActive: boolean = useIsMarkActive(editor, type);

  const Icon = MARK_ICONS[type];
  const shortcutKey: string | undefined = MARK_SHORTCUTS[type];
  const formattedName: string = getFormattedMarkName(type);

  const show: boolean = useMemo(() => {
    return shouldShowMarkButton(editor, markInSchema);
  }, [editor, markInSchema]);

  return {
    show,
    markInSchema,
    isDisabled,
    isActive,
    Icon,
    shortcutKey,
    formattedName,
  };
}

function useMarkButtonClick(onClick: MarkButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function MarkButton(markButtonProps: MarkButtonProps) {
  const { editor: providedEditor, onClick, disabled, type, children, ...props } = markButtonProps;
  const editor = useTiptapEditor(providedEditor);

  const { show, isDisabled, isActive, Icon, shortcutKey, formattedName } = useMarkState(editor, type, disabled);

  const handleClick = useMarkButtonClick(onClick, () => {
    toggleMark(editor, type);
  });

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          data-slot="mark-button"
          disabled={isDisabled}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          aria-label={type}
          aria-pressed={isActive}
          onClick={handleClick}
          {...props}
        >
          {children || <Icon />}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {formattedName}
        <p className="text-xs opacity-70">{shortcutKey}</p>
      </TooltipContent>
    </Tooltip>
  );
}
